
export type BizType =
  | 'detailing'
  | 'marine_detailing'
  | 'pressure_washing'
  | 'roof_washing'
  | 'lawn_care'
  | 'handyman'
  | 'painting'
  | 'plumbing'
  | 'electrical'
  | 'hairdresser'
  | 'support_worker';

export interface ServiceTier {
  name: string;
  description: string;
  includes: string[];
}

export interface PricingRow {
  label: string;
  sublabel?: string;
  tiers: Record<string, number | string>;
}

export interface TrustPoint {
  icon: string;
  title: string;
  body: string;
}

export interface BusinessTemplate {
  badge: string;
  headline: string;
  headlineAccent: string;
  tagline: string;
  ctaLabel: string;
  ctaRoute: 'booking' | 'quote-request';
  pricingMode: 'fixed' | 'quote' | 'both';
  pricingTitle: string;
  pricingSubtitle: string;
  tiers: { name: string; color: string }[];
  pricingRows: PricingRow[];
  serviceTiers: ServiceTier[];
  trustPoints: TrustPoint[];
  quoteLineItemPresets: { description: string; unit: string; defaultQty: number; defaultPrice: number }[];
}

export const BUSINESS_TEMPLATES: Record<string, BusinessTemplate> = {

  detailing: {
    badge: 'Mobile Auto & Marine Detailing',
    headline: 'Detail that',
    headlineAccent: 'Delivers.',
    tagline: 'Professional mobile detailing for cars, SUVs, 4WDs, boats and jet skis. We come to you — driveway, marina, or workplace.',
    ctaLabel: 'Book Your Detail',
    ctaRoute: 'booking',
    pricingMode: 'fixed',
    pricingTitle: 'Services & Pricing',
    pricingSubtitle: 'Fair, size-based pricing. No hidden fees.',
    tiers: [
      { name: 'Basic Detail', color: 'text-purple-600' },
      { name: 'Full Detail', color: 'text-teal-600' },
    ],
    pricingRows: [
      { label: 'Small Car', sublabel: 'Yaris, Swift, Polo', tiers: { 'Basic Detail': 149, 'Full Detail': 210 } },
      { label: 'Medium Car', sublabel: 'Corolla, i30, Civic', tiers: { 'Basic Detail': 159, 'Full Detail': 230 } },
      { label: 'Large / SUV', sublabel: 'RAV4, Hilux, Ranger', tiers: { 'Basic Detail': 169, 'Full Detail': 240 } },
      { label: 'X-Large', sublabel: 'LandCruiser, Patrol, Carnival', tiers: { 'Basic Detail': 189, 'Full Detail': 260 } },
      { label: 'Boat / Jet Ski', sublabel: 'Hull, deck & interior', tiers: { 'Basic Detail': 'POA', 'Full Detail': 'POA' } },
    ],
    serviceTiers: [
      {
        name: 'Basic Detail',
        description: 'The essential clean — inside and out.',
        includes: ['Exterior hand wash & dry', 'Full interior vacuum', 'Door jambs wiped', 'Windows inside & out', 'Tyres dressed'],
      },
      {
        name: 'Full Detail',
        description: 'Showroom finish, delivered to you.',
        includes: ['Everything in Basic +', 'Dashboard deep clean', 'Interior plastics protected', 'Deodorising treatment', 'Detailed wheel clean', 'Engine bay wipe-down'],
      },
    ],
    trustPoints: [
      { icon: 'clock', title: 'We Come to You', body: 'Detailing at your driveway, marina, or workplace. No drop-off required.' },
      { icon: 'shield', title: 'Fully Insured', body: 'Public liability covered. Your vehicle or vessel is in safe hands.' },
      { icon: 'map', title: 'Local & Independent', body: 'Supporting small, locally-owned businesses in your community.' },
    ],
    quoteLineItemPresets: [
      { description: 'Basic Detail — Small Car', unit: 'vehicle', defaultQty: 1, defaultPrice: 149 },
      { description: 'Basic Detail — Medium Car', unit: 'vehicle', defaultQty: 1, defaultPrice: 159 },
      { description: 'Full Detail — SUV/4WD', unit: 'vehicle', defaultQty: 1, defaultPrice: 240 },
      { description: 'Boat / Marine Detail', unit: 'vessel', defaultQty: 1, defaultPrice: 0 },
      { description: 'Engine Bay Clean', unit: 'job', defaultQty: 1, defaultPrice: 80 },
      { description: 'Odour Treatment', unit: 'job', defaultQty: 1, defaultPrice: 60 },
      { description: 'Travel Fee', unit: 'job', defaultQty: 1, defaultPrice: 20 },
    ],
  },

  pressure_washing: {
    badge: 'Mobile Pressure Washing',
    headline: 'Blast away the',
    headlineAccent: 'Grime.',
    tagline: 'High-pressure cleaning for driveways, patios, roofs, fences, gutters and commercial surfaces. Fast, thorough, and completely mobile.',
    ctaLabel: 'Get a Free Quote',
    ctaRoute: 'quote-request',
    pricingMode: 'quote',
    pricingTitle: 'What We Clean',
    pricingSubtitle: 'Prices vary by size and condition. We quote on-site or by photo.',
    tiers: [
      { name: 'Standard Clean', color: 'text-blue-600' },
      { name: 'Deep Clean', color: 'text-teal-600' },
    ],
    pricingRows: [
      { label: 'Driveway', sublabel: 'Up to 50m²', tiers: { 'Standard Clean': 120, 'Deep Clean': 180 } },
      { label: 'Patio / Deck', sublabel: 'Up to 40m²', tiers: { 'Standard Clean': 100, 'Deep Clean': 160 } },
      { label: 'Roof Wash', sublabel: 'Single storey', tiers: { 'Standard Clean': 350, 'Deep Clean': 550 } },
      { label: 'Fence Line', sublabel: 'Per 10m', tiers: { 'Standard Clean': 60, 'Deep Clean': 90 } },
      { label: 'Commercial', sublabel: 'Quoted on site', tiers: { 'Standard Clean': 'POA', 'Deep Clean': 'POA' } },
    ],
    serviceTiers: [
      {
        name: 'Standard Clean',
        description: 'Removes surface dirt, mildew and general grime.',
        includes: ['High-pressure rinse', 'Surface degreaser applied', 'Perimeter edges cleared', 'Safe for treated timber & pavers'],
      },
      {
        name: 'Deep Clean',
        description: 'Full treatment including staining and bio-growth.',
        includes: ['Everything in Standard +', 'Fungicide / algaecide treatment', 'Oil stain pre-treatment', 'Post-clean sealant option', 'Before & after photos provided'],
      },
    ],
    trustPoints: [
      { icon: 'droplet', title: 'Eco-Friendly Products', body: 'Biodegradable detergents. Safe for gardens, pets, and waterways.' },
      { icon: 'shield', title: 'Insured & Professional', body: 'All work carried out by trained operators with public liability cover.' },
      { icon: 'zap', title: 'Fast Turnaround', body: 'Most jobs completed in a single visit. Same-week bookings available.' },
    ],
    quoteLineItemPresets: [
      { description: 'Driveway Pressure Wash', unit: 'm²', defaultQty: 50, defaultPrice: 2.4 },
      { description: 'Patio / Deck Wash', unit: 'm²', defaultQty: 40, defaultPrice: 2.5 },
      { description: 'Roof Wash — Single Storey', unit: 'job', defaultQty: 1, defaultPrice: 350 },
      { description: 'Roof Wash — Double Storey', unit: 'job', defaultQty: 1, defaultPrice: 550 },
      { description: 'Fence Line Wash', unit: 'per 10m', defaultQty: 1, defaultPrice: 60 },
      { description: 'Algae / Mould Treatment', unit: 'job', defaultQty: 1, defaultPrice: 80 },
      { description: 'Oil Stain Pre-treatment', unit: 'spot', defaultQty: 1, defaultPrice: 45 },
      { description: 'Post-clean Sealant', unit: 'm²', defaultQty: 50, defaultPrice: 3 },
      { description: 'Travel Fee', unit: 'job', defaultQty: 1, defaultPrice: 25 },
    ],
  },

  roof_washing: {
    badge: 'Roof Washing & Restoration',
    headline: 'Your roof,',
    headlineAccent: 'Restored.',
    tagline: 'Soft-wash roof cleaning that removes lichen, moss, and algae without damaging your tiles or voiding your warranty.',
    ctaLabel: 'Get a Roof Quote',
    ctaRoute: 'quote-request',
    pricingMode: 'quote',
    pricingTitle: 'Roof Cleaning Packages',
    pricingSubtitle: 'Quoted by roof size and pitch. All quotes include a site inspection.',
    tiers: [
      { name: 'Soft Wash', color: 'text-sky-600' },
      { name: 'Full Restore', color: 'text-teal-600' },
    ],
    pricingRows: [
      { label: 'Small Home', sublabel: 'Up to 120m² roof', tiers: { 'Soft Wash': 350, 'Full Restore': 650 } },
      { label: 'Medium Home', sublabel: '120–200m² roof', tiers: { 'Soft Wash': 500, 'Full Restore': 900 } },
      { label: 'Large Home', sublabel: '200m²+ roof', tiers: { 'Soft Wash': 'POA', 'Full Restore': 'POA' } },
      { label: 'Gutters Cleared', sublabel: 'While on site', tiers: { 'Soft Wash': 120, 'Full Restore': 120 } },
    ],
    serviceTiers: [
      {
        name: 'Soft Wash',
        description: 'Low-pressure treatment — tile-safe and warranty-friendly.',
        includes: ['Biodegradable soft-wash solution', 'Lichen & moss treatment', 'Ridge cap inspection', 'Gutter flush included'],
      },
      {
        name: 'Full Restore',
        description: 'Deep clean plus protective coating.',
        includes: ['Everything in Soft Wash +', 'Moss & lichen physically removed', 'Anti-fungal sealer applied', 'Colour rejuvenation coat', '2-year treatment guarantee'],
      },
    ],
    trustPoints: [
      { icon: 'shield', title: 'Warranty Safe', body: 'Soft-wash method approved for all major tile manufacturers.' },
      { icon: 'clock', title: '2-Year Guarantee', body: 'Full Restore package includes a 2-year re-growth guarantee.' },
      { icon: 'map', title: 'Licensed & Insured', body: 'Work at heights certified. Full public liability and property damage cover.' },
    ],
    quoteLineItemPresets: [
      { description: 'Roof Soft Wash — Small Home', unit: 'job', defaultQty: 1, defaultPrice: 350 },
      { description: 'Roof Soft Wash — Medium Home', unit: 'job', defaultQty: 1, defaultPrice: 500 },
      { description: 'Roof Full Restore', unit: 'job', defaultQty: 1, defaultPrice: 900 },
      { description: 'Gutter Clear & Flush', unit: 'job', defaultQty: 1, defaultPrice: 120 },
      { description: 'Anti-fungal Sealer', unit: 'm²', defaultQty: 150, defaultPrice: 2 },
      { description: 'Ridge Cap Repoint (per metre)', unit: 'm', defaultQty: 10, defaultPrice: 25 },
      { description: 'Travel Fee', unit: 'job', defaultQty: 1, defaultPrice: 25 },
    ],
  },

  lawn_care: {
    badge: 'Lawn & Garden Maintenance',
    headline: 'Lawns that make the',
    headlineAccent: 'Neighbours look.',
    tagline: 'Regular mowing, edging, pruning, garden beds and yard clean-ups. Reliable, local, and always on time.',
    ctaLabel: 'Book a Mow',
    ctaRoute: 'booking',
    pricingMode: 'fixed',
    pricingTitle: 'Lawn Care Packages',
    pricingSubtitle: 'Flat-rate pricing by property size. No surprises.',
    tiers: [
      { name: 'Mow & Edge', color: 'text-green-600' },
      { name: 'Full Tidy', color: 'text-teal-600' },
    ],
    pricingRows: [
      { label: 'Small Block', sublabel: 'Up to 300m²', tiers: { 'Mow & Edge': 65, 'Full Tidy': 110 } },
      { label: 'Medium Block', sublabel: '300–600m²', tiers: { 'Mow & Edge': 90, 'Full Tidy': 150 } },
      { label: 'Large Block', sublabel: '600m²–1 acre', tiers: { 'Mow & Edge': 130, 'Full Tidy': 220 } },
      { label: 'Acreage', sublabel: '1 acre+', tiers: { 'Mow & Edge': 'POA', 'Full Tidy': 'POA' } },
    ],
    serviceTiers: [
      {
        name: 'Mow & Edge',
        description: 'The weekly essential — clean and tidy every time.',
        includes: ['Lawn mowed to height', 'All edges trimmed', 'Paths & driveways blown clear', 'Clippings removed'],
      },
      {
        name: 'Full Tidy',
        description: 'Full property maintenance in one visit.',
        includes: ['Everything in Mow & Edge +', 'Garden bed weeding', 'Shrub & hedge trim', 'Rubbish removal', 'Fertiliser applied (optional)'],
      },
    ],
    trustPoints: [
      { icon: 'clock', title: 'Regular Schedules', body: 'Weekly, fortnightly, or monthly. We show up when we say we will.' },
      { icon: 'shield', title: 'Fully Insured', body: 'Public liability covered. Your property is protected.' },
      { icon: 'leaf', title: 'Eco Conscious', body: 'We bag clippings for composting and use low-emission equipment where possible.' },
    ],
    quoteLineItemPresets: [
      { description: 'Mow & Edge — Small Block', unit: 'visit', defaultQty: 1, defaultPrice: 65 },
      { description: 'Mow & Edge — Medium Block', unit: 'visit', defaultQty: 1, defaultPrice: 90 },
      { description: 'Full Tidy — Medium Block', unit: 'visit', defaultQty: 1, defaultPrice: 150 },
      { description: 'Hedge / Shrub Trim', unit: 'hour', defaultQty: 1, defaultPrice: 65 },
      { description: 'Garden Bed Weed & Mulch', unit: 'm²', defaultQty: 20, defaultPrice: 8 },
      { description: 'Rubbish Removal', unit: 'trailer load', defaultQty: 1, defaultPrice: 120 },
      { description: 'Fertiliser Application', unit: 'job', defaultQty: 1, defaultPrice: 45 },
      { description: 'Travel Fee', unit: 'job', defaultQty: 1, defaultPrice: 20 },
    ],
  },

  handyman: {
    badge: 'General Trades & Handyman',
    headline: 'One tradie,',
    headlineAccent: 'Any job.',
    tagline: 'Reliable handyman and trade services for homes and businesses. Furniture assembly to full renovations — no job too big or too small.',
    ctaLabel: 'Get a Quote',
    ctaRoute: 'quote-request',
    pricingMode: 'quote',
    pricingTitle: 'Common Services',
    pricingSubtitle: 'Hourly rates or fixed-price quotes. We scope every job fairly.',
    tiers: [
      { name: 'Hourly Rate', color: 'text-orange-600' },
      { name: 'Fixed Price', color: 'text-teal-600' },
    ],
    pricingRows: [
      { label: 'General Handyman', sublabel: 'Repairs, installs, assembly', tiers: { 'Hourly Rate': 95, 'Fixed Price': 'Quoted' } },
      { label: 'Carpentry', sublabel: 'Decking, framing, fit-out', tiers: { 'Hourly Rate': 110, 'Fixed Price': 'Quoted' } },
      { label: 'Tiling', sublabel: 'Floor & wall', tiers: { 'Hourly Rate': 'POA', 'Fixed Price': 'Quoted' } },
      { label: 'Painting', sublabel: 'Interior & exterior', tiers: { 'Hourly Rate': 75, 'Fixed Price': 'Quoted' } },
      { label: 'Flat-pack Assembly', sublabel: 'Per item', tiers: { 'Hourly Rate': 'N/A', 'Fixed Price': 65 } },
    ],
    serviceTiers: [
      {
        name: 'Hourly Rate Jobs',
        description: 'Best for small repairs, installs, and odd jobs.',
        includes: ['Min. 1 hour call-out', 'All labour included', 'Materials quoted separately', 'Invoice provided on completion'],
      },
      {
        name: 'Fixed Price Jobs',
        description: 'Scoped projects with a locked-in price.',
        includes: ['Site visit or photo quote', 'Written quote before work begins', 'Materials & labour included', 'No surprise charges'],
      },
    ],
    trustPoints: [
      { icon: 'shield', title: 'Licensed & Insured', body: 'All trade work completed to code. Public liability and warranty on workmanship.' },
      { icon: 'clock', title: 'We Show Up', body: 'On time, every time. Tradies with a reputation worth keeping.' },
      { icon: 'zap', title: 'One Call Does It All', body: 'From a leaking tap to a full reno — we coordinate what we can\'t do ourselves.' },
    ],
    quoteLineItemPresets: [
      { description: 'General Labour', unit: 'hour', defaultQty: 2, defaultPrice: 95 },
      { description: 'Carpentry Labour', unit: 'hour', defaultQty: 2, defaultPrice: 110 },
      { description: 'Flat-pack Assembly', unit: 'item', defaultQty: 1, defaultPrice: 65 },
      { description: 'Painting — Interior Wall', unit: 'm²', defaultQty: 20, defaultPrice: 12 },
      { description: 'Tiling — Floor', unit: 'm²', defaultQty: 10, defaultPrice: 85 },
      { description: 'Materials (estimate)', unit: 'job', defaultQty: 1, defaultPrice: 150 },
      { description: 'Call-out / Travel Fee', unit: 'job', defaultQty: 1, defaultPrice: 35 },
      { description: 'Rubbish Removal', unit: 'load', defaultQty: 1, defaultPrice: 120 },
    ],
  },

  painting: {
    badge: 'Professional Painting Services',
    headline: 'Fresh coat,',
    headlineAccent: 'Fresh start.',
    tagline: 'Interior and exterior painting for homes and commercial spaces. Clean lines, quality finishes, and a price you can rely on.',
    ctaLabel: 'Get a Paint Quote',
    ctaRoute: 'quote-request',
    pricingMode: 'quote',
    pricingTitle: 'Painting Services',
    pricingSubtitle: 'Quoted by area. We measure before we price.',
    tiers: [
      { name: 'Interior', color: 'text-purple-600' },
      { name: 'Exterior', color: 'text-teal-600' },
    ],
    pricingRows: [
      { label: 'Single Room', sublabel: 'Walls only', tiers: { 'Interior': 350, 'Exterior': 'N/A' } },
      { label: 'Full House Interior', sublabel: '3 bed home', tiers: { 'Interior': 2800, 'Exterior': 'N/A' } },
      { label: 'Full House Exterior', sublabel: '3 bed home', tiers: { 'Interior': 'N/A', 'Exterior': 3500 } },
      { label: 'Fence / Deck', sublabel: 'Per job', tiers: { 'Interior': 'N/A', 'Exterior': 'Quoted' } },
      { label: 'Commercial', sublabel: 'Office / retail', tiers: { 'Interior': 'POA', 'Exterior': 'POA' } },
    ],
    serviceTiers: [
      {
        name: 'Interior Painting',
        description: 'Clean, precise interior finishes.',
        includes: ['Walls, ceilings & trims', 'Surface prep & gap filling', 'Premium paint supplied', '2 coats standard', 'Furniture moved & protected'],
      },
      {
        name: 'Exterior Painting',
        description: 'Weather-resistant exterior coatings.',
        includes: ['High-pressure wash first', 'Scrape, sand & prime', 'Premium exterior paint', 'Window & door masking', '5-year finish warranty'],
      },
    ],
    trustPoints: [
      { icon: 'shield', title: 'Licensed Painters', body: 'Qualified tradespeople. All work meets Australian Standards.' },
      { icon: 'star', title: 'Premium Products Only', body: 'We use Dulux and Taubmans premium ranges. No cutting corners on paint.' },
      { icon: 'clock', title: 'On-Time Completion', body: 'We give you a start date and a finish date and we stick to both.' },
    ],
    quoteLineItemPresets: [
      { description: 'Interior Wall Paint (per room)', unit: 'room', defaultQty: 1, defaultPrice: 350 },
      { description: 'Ceiling Paint', unit: 'room', defaultQty: 1, defaultPrice: 180 },
      { description: 'Trim & Skirting', unit: 'room', defaultQty: 1, defaultPrice: 120 },
      { description: 'Exterior Wall Paint', unit: 'm²', defaultQty: 50, defaultPrice: 18 },
      { description: 'Surface Prep & Primer', unit: 'm²', defaultQty: 50, defaultPrice: 8 },
      { description: 'Gap Fill & Patch', unit: 'job', defaultQty: 1, defaultPrice: 150 },
      { description: 'Paint Supply (premium)', unit: '10L', defaultQty: 2, defaultPrice: 95 },
      { description: 'Travel Fee', unit: 'job', defaultQty: 1, defaultPrice: 35 },
    ],
  },

  hairdresser: {
    badge: 'Mobile & In-Home Hairdressing',
    headline: 'Salon quality,',
    headlineAccent: 'Your space.',
    tagline: 'Professional hair services at your home, office, or event. Cuts, colour, styling and more — no travel stress, no waiting room.',
    ctaLabel: 'Book an Appointment',
    ctaRoute: 'booking',
    pricingMode: 'fixed',
    pricingTitle: 'Services & Pricing',
    pricingSubtitle: 'Straightforward pricing. What you see is what you pay.',
    tiers: [
      { name: 'Cut & Style', color: 'text-pink-600' },
      { name: 'Colour & Treatment', color: 'text-purple-600' },
    ],
    pricingRows: [
      { label: "Women's Cut & Blowdry", sublabel: 'Wash, cut, style', tiers: { 'Cut & Style': 95, 'Colour & Treatment': 'N/A' } },
      { label: "Men's Cut", sublabel: 'Scissors or clipper', tiers: { 'Cut & Style': 55, 'Colour & Treatment': 'N/A' } },
      { label: 'Full Colour', sublabel: 'All-over colour', tiers: { 'Cut & Style': 'N/A', 'Colour & Treatment': 165 } },
      { label: 'Highlights / Balayage', sublabel: 'Partial or full', tiers: { 'Cut & Style': 'N/A', 'Colour & Treatment': 220 } },
      { label: 'Treatment / Keratin', sublabel: 'Smoothing or repair', tiers: { 'Cut & Style': 'N/A', 'Colour & Treatment': 180 } },
    ],
    serviceTiers: [
      {
        name: 'Cut & Style',
        description: 'Expert cuts and styling in the comfort of your home.',
        includes: ['Wash & condition', 'Precision cut', 'Blowdry & style', 'Product recommendations', 'All tools & products supplied'],
      },
      {
        name: 'Colour & Treatment',
        description: 'Full colour services with professional products.',
        includes: ['Colour consultation', 'Premium colour applied', 'Processing & toning', 'Wash, treatment & blowdry', 'Aftercare advice'],
      },
    ],
    trustPoints: [
      { icon: 'shield', title: 'Qualified Stylist', body: 'Fully qualified with years of salon experience. Insurance covered.' },
      { icon: 'clock', title: 'We Come to You', body: 'Your home, aged care facility, workplace, or event. We bring everything.' },
      { icon: 'star', title: 'Professional Products', body: 'Salon-grade colour and treatment brands only. No compromises.' },
    ],
    quoteLineItemPresets: [
      { description: "Women's Cut & Blowdry", unit: 'service', defaultQty: 1, defaultPrice: 95 },
      { description: "Men's Haircut", unit: 'service', defaultQty: 1, defaultPrice: 55 },
      { description: "Children's Cut", unit: 'service', defaultQty: 1, defaultPrice: 40 },
      { description: 'Full Colour', unit: 'service', defaultQty: 1, defaultPrice: 165 },
      { description: 'Partial Highlights', unit: 'service', defaultQty: 1, defaultPrice: 150 },
      { description: 'Full Highlights / Balayage', unit: 'service', defaultQty: 1, defaultPrice: 220 },
      { description: 'Keratin / Smoothing Treatment', unit: 'service', defaultQty: 1, defaultPrice: 180 },
      { description: 'Travel Fee', unit: 'job', defaultQty: 1, defaultPrice: 25 },
    ],
  },
};

// Biz type mapping (onboarding values to template keys)
const BIZ_TYPE_MAP: Record<string, string> = {
  'mowing': 'lawn_care',
  'cleaning': 'cleaning',
  'detailing': 'detailing',
  'painting': 'painting',
  'handyman': 'handyman',
  'support_worker': 'support_worker',
};

// Fallback for unmapped biz_types
export const getTemplate = (bizType: string): BusinessTemplate => {
  const mappedKey = BIZ_TYPE_MAP[bizType] || bizType;
  return BUSINESS_TEMPLATES[mappedKey] ?? BUSINESS_TEMPLATES['handyman'];
};